import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  StarIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  ChartBarIcon,
  QrCodeIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  CalendarIcon,
  SparklesIcon,
  ClockIcon,
  HandThumbUpIcon,
  EyeIcon,
  BuildingOffice2Icon,
  DevicePhoneMobileIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import Breadcrumb from '../../components/Navigation/Breadcrumb';
import InternalLinks from '../../components/Navigation/InternalLinks';
import { WebPageSchema, BreadcrumbSchema } from '../../components/SEO/StructuredData';

gsap.registerPlugin(ScrollTrigger);

const PAGE_PATH = '/dental-google-reviews';
const PAGE_URL = `https://smileconsent.co.uk${PAGE_PATH}`;
const PAGE_TITLE =
  'Google Reviews for Dental Practices | Request Reviews After Consent | Smile Consent';
const PAGE_DESCRIPTION =
  'Collect more Google reviews for your dental practice. SmileConsent sends branded review requests after signed consent, with private feedback to protect your reputation and tracking so your team knows what works.';

const features = [
  {
    icon: EnvelopeIcon,
    title: 'One-click review requests',
    description:
      'After a patient signs consent, your team can send a branded Google review invitation in seconds — patient email is filled in automatically.',
    points: [
      'Send from My Consents or consent detail',
      'Resend follow-ups when needed',
      'No copy-paste of Google links for every patient',
    ],
  },
  {
    icon: ShieldCheckIcon,
    title: 'Private feedback first',
    description:
      'Happy patients can leave a public Google review. Patients with concerns can send private feedback that never appears on Google — so you hear the truth before the internet does.',
    points: [
      'Neutral invitation: Google or private',
      'Inbox for private practice feedback',
      'Convert positive private feedback into a Google ask',
    ],
  },
  {
    icon: ChartBarIcon,
    title: 'Delivery & engagement tracking',
    description:
      'See whether the email was opened, the review link was clicked, or the patient went through to Google — right on the consent record.',
    points: [
      'Sent → email opened → link opened status',
      'Consent activity timeline events',
      'Know which requests need a polite follow-up',
    ],
  },
  {
    icon: QrCodeIcon,
    title: 'Reception QR & shareable page',
    description:
      'Paste your Google Business Profile review URL once. Share a branded patient page and QR code for reception desks and treatment rooms.',
    points: [
      'One-time Google link setup',
      'Clinic logo and brand colours on the patient page',
      'QR for walk-out or waiting-room prompts',
    ],
  },
  {
    icon: HandThumbUpIcon,
    title: 'Turn private praise into public reviews',
    description:
      'When a patient leaves positive private feedback, ask them to leave a Google review with a deep link that takes them straight there.',
    points: [
      '“Ask for Google review” from the feedback inbox',
      'Thank-you style follow-up email',
      'Tracks engagement on the second ask',
    ],
  },
  {
    icon: DevicePhoneMobileIcon,
    title: 'Built into your consent workflow',
    description:
      'Review requests sit next to signed consents — not in a separate spreadsheet or third-party tool your team forgets to open.',
    points: [
      'Works after treatment consent is signed',
      'Included with SmileConsent for UK practices',
      'Same login your team already uses',
    ],
  },
];

const steps = [
  {
    step: '1',
    title: 'Connect your Google review link',
    description:
      'Enable Google review requests and paste your Google Business Profile review URL once. SmileConsent validates it and saves it for your practice.',
  },
  {
    step: '2',
    title: 'Patient signs consent',
    description:
      'Complete your usual SmileConsent workflow. When the consent is signed, a review request becomes available for that patient.',
  },
  {
    step: '3',
    title: 'Send a branded invitation',
    description:
      'Staff send a short email — “How was your visit?” — linking to your branded feedback page with Google review and private feedback options.',
  },
  {
    step: '4',
    title: 'Grow reviews. Protect reputation.',
    description:
      'Public reviews land on Google. Private feedback lands in your inbox. Track opens and clicks, then follow up with happy patients who have not reviewed yet.',
  },
];

const painPoints = [
  {
    icon: ClockIcon,
    title: 'Manual asks get forgotten',
    text: 'Reception is busy. Clinicians move on. The perfect moment after a great visit disappears — and so does the review.',
  },
  {
    icon: EyeIcon,
    title: 'You only hear from the unhappy',
    text: 'Dissatisfied patients post publicly. Satisfied patients rarely do — unless you make it easy at the right time.',
  },
  {
    icon: LockClosedIcon,
    title: 'Direct links risk public complaints',
    text: 'Sending only a Google link funnels every emotion onto your public profile. A private channel keeps constructive feedback in-house.',
  },
];

const faqs = [
  {
    question: 'How do Google review requests work in SmileConsent?',
    answer:
      'Once your practice enables the feature and saves a Google Business Profile review URL, staff can send a branded email from a signed consent. The patient opens a clinic-branded page and chooses to leave a Google review or send private feedback.',
  },
  {
    question: 'Can patients leave private feedback instead of a Google review?',
    answer:
      'Yes. Every invitation offers both options. Private feedback is not published on Google — it goes to your practice inbox so you can respond and improve care quietly.',
  },
  {
    question: 'Will this help us get more Google reviews for our dental practice?',
    answer:
      'SmileConsent removes friction: timed asks after signed consent, one-click sending, a branded mobile page, QR codes for reception, and follow-ups for patients who left positive private feedback. Practices that ask consistently after positive visits typically collect more reviews than those relying on memory alone.',
  },
  {
    question: 'Can we track whether patients open the review email?',
    answer:
      'Yes. SmileConsent tracks email opens, review-link opens, and Google clicks, and shows status on the consent record so your team knows when a polite reminder makes sense.',
  },
  {
    question: 'Is this only for private practices?',
    answer:
      'No. Any UK dental practice using SmileConsent can enable Google review requests — NHS, private, or mixed — as long as you have a Google Business Profile review link.',
  },
  {
    question: 'How long does setup take?',
    answer:
      'Usually a few minutes: toggle the feature on, paste your Google review URL from Google Business Profile, save, then optionally print the QR code for reception.',
  },
];

export default function DentalGoogleReviews() {
  const navigate = useNavigate();

  usePageMeta(PAGE_TITLE, PAGE_DESCRIPTION, PAGE_URL);

  useEffect(() => {
    gsap.utils.toArray('.animate-on-scroll').forEach((element) => {
      gsap.fromTo(
        element,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: element,
            start: 'top 80%',
            end: 'bottom 20%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    });
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta
          name="keywords"
          content="google reviews dental practice, dental google reviews, get more google reviews dentist, google review requests for dentists, dental practice reputation, ask patients for google reviews, google reviews after treatment, smileconsent google reviews"
        />
        <link rel="canonical" href={PAGE_URL} />

        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://smileconsent.co.uk/og-image.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content="https://smileconsent.co.uk/og-image.png" />

        <script type="application/ld+json">
          {JSON.stringify(
            WebPageSchema({
              name: 'Google Reviews for Dental Practices',
              url: PAGE_URL,
              description: PAGE_DESCRIPTION,
            })
          )}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(
            BreadcrumbSchema({
              items: [{ label: 'Dental Google Reviews', path: PAGE_PATH }],
            })
          )}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <Navigation />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <Breadcrumb items={[{ label: 'Dental Google Reviews', path: PAGE_PATH }]} />
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-br from-amber-50 via-blue-50 to-indigo-100 py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <p className="inline-flex items-center gap-2 text-sm font-semibold text-amber-800 bg-amber-100/80 border border-amber-200 rounded-full px-4 py-1.5 mb-6">
                <StarIcon className="h-4 w-4" aria-hidden />
                Google reviews built into your consent workflow
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6">
                Get more Google reviews for your{' '}
                <span className="text-blue-600">dental practice</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-4 leading-relaxed">
                SmileConsent asks patients for a Google review after signed consent — with a private
                feedback option that protects your reputation, and tracking so nothing falls through
                the cracks.
              </p>
              <p className="text-base text-gray-500 mb-8 max-w-2xl mx-auto">
                One Google Business Profile link. Branded patient page. One-click sends from your
                team. Built for UK dentists who want more five-star visibility without chasing
                reviews by hand.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register"
                  className="bg-blue-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 shadow-lg"
                >
                  <SparklesIcon className="h-6 w-6" aria-hidden />
                  Start Free Trial
                </Link>
                <button
                  type="button"
                  onClick={() => navigate('/book-demo')}
                  className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                >
                  <CalendarIcon className="h-6 w-6" aria-hidden />
                  Book Demo
                </button>
              </div>
              <p className="mt-4 text-sm text-gray-500">
                No credit card required · Set up your Google review link in minutes
              </p>
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
              <div>
                <p className="text-2xl font-bold">Ask at the right time</p>
                <p className="text-blue-100 text-sm mt-1">After consent is signed — when the visit is fresh</p>
              </div>
              <div>
                <p className="text-2xl font-bold">Protect your reputation</p>
                <p className="text-blue-100 text-sm mt-1">Private feedback stays off Google</p>
              </div>
              <div>
                <p className="text-2xl font-bold">See what works</p>
                <p className="text-blue-100 text-sm mt-1">Track opens, clicks, and Google visits</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pain */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 animate-on-scroll">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why most dental practices struggle with Google reviews
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Patients choose dentists with strong Google ratings. Without a simple system after
                treatment, review requests stay as sticky notes and good intentions.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {painPoints.map((item) => (
                <div
                  key={item.title}
                  className="rounded-2xl border border-gray-200 bg-gray-50 p-8 animate-on-scroll"
                >
                  <div className="bg-amber-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="h-6 w-6 text-amber-700" aria-hidden />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 animate-on-scroll">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                How SmileConsent Google review requests work
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From Google Business Profile link to patient inbox — a four-step loop your team can
                run every day without a separate reviews app.
              </p>
            </div>
            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((item) => (
                <li
                  key={item.step}
                  className="relative bg-white rounded-2xl border border-gray-200 p-6 shadow-sm animate-on-scroll"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white font-bold mb-4">
                    {item.step}
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 animate-on-scroll">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Google review features built for dental teams
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Everything you need to request reviews, capture private feedback, and measure
                follow-through — without leaving SmileConsent.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature) => (
                <article
                  key={feature.title}
                  className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100 animate-on-scroll"
                >
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-100 p-3 rounded-lg mr-4">
                      <feature.icon className="h-8 w-8 text-blue-600" aria-hidden />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed">{feature.description}</p>
                  <ul className="space-y-2">
                    {feature.points.map((point) => (
                      <li key={point} className="flex items-start text-sm text-gray-600">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" aria-hidden />
                        {point}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation callout */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="animate-on-scroll">
                <div className="inline-flex items-center gap-2 text-emerald-800 font-semibold text-sm mb-4">
                  <ChatBubbleLeftRightIcon className="h-5 w-5" aria-hidden />
                  Reputation protection
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Public stars for happy patients. Private inbox for honest concerns.
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  A single “leave us a Google review” link sends every emotion to your public
                  profile. SmileConsent’s patient page offers a choice: celebrate the practice on
                  Google, or tell you privately what could be better.
                </p>
                <ul className="space-y-3 text-gray-700 mb-8">
                  <li className="flex gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" aria-hidden />
                    <span>
                      Resolve issues in-house before they become one-star posts
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" aria-hidden />
                    <span>
                      Follow up with patients who left positive private feedback and invite a Google
                      review
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" aria-hidden />
                    <span>
                      Keep the tone neutral — patients feel respected, not pressured
                    </span>
                  </li>
                </ul>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 font-semibold text-emerald-700 hover:text-emerald-900"
                >
                  Start collecting smarter feedback
                  <ArrowRightIcon className="h-4 w-4" aria-hidden />
                </Link>
              </div>
              <div className="bg-white rounded-2xl border border-emerald-100 shadow-xl p-8 animate-on-scroll">
                <p className="text-sm font-medium text-gray-500 mb-4">What patients see</p>
                <div className="space-y-4">
                  <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-5">
                    <div className="flex items-center gap-2 text-amber-800 font-semibold mb-1">
                      <StarIcon className="h-5 w-5" aria-hidden />
                      Leave a Google review
                    </div>
                    <p className="text-sm text-amber-900/80">
                      Opens your Google Business Profile so other patients can find you
                    </p>
                  </div>
                  <div className="rounded-xl border-2 border-slate-200 bg-slate-50 p-5">
                    <div className="flex items-center gap-2 text-slate-800 font-semibold mb-1">
                      <LockClosedIcon className="h-5 w-5" aria-hidden />
                      Send private feedback
                    </div>
                    <p className="text-sm text-slate-600">
                      Shared only with your practice — not published on Google
                    </p>
                  </div>
                </div>
                <p className="mt-6 text-xs text-gray-500 text-center">
                  Powered by SmileConsent · Branded with your clinic name and logo
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Consent + reviews pairing */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-8 md:p-12 animate-on-scroll">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <BuildingOffice2Icon className="h-10 w-10 text-blue-200 mb-4" aria-hidden />
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    Consent done. Review asked. One platform.
                  </h2>
                  <p className="text-lg text-blue-100 leading-relaxed mb-6">
                    SmileConsent already handles AI-assisted dental consent, digital signing, and
                    audit-ready records. Google review requests close the loop after treatment —
                    so reputation growth happens in the same system your team opens every day.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/register"
                      className="inline-flex items-center justify-center gap-2 bg-white text-blue-700 px-6 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
                    >
                      Start Free Trial
                    </Link>
                    <Link
                      to="/dental-consent-software"
                      className="inline-flex items-center justify-center gap-2 border-2 border-white/70 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors"
                    >
                      Explore consent software
                    </Link>
                  </div>
                </div>
                <ul className="space-y-4">
                  {[
                    'AI-assisted consent drafts with clinician review',
                    'Digital patient signing & activity timeline',
                    'Google review requests after signed consents',
                    'Private feedback inbox + follow-up Google asks',
                  ].map((line) => (
                    <li key={line} className="flex items-start gap-3 bg-white/10 rounded-xl px-4 py-3">
                      <CheckCircleIcon className="h-6 w-6 text-emerald-300 shrink-0" aria-hidden />
                      <span className="text-blue-50">{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 animate-on-scroll">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently asked questions
              </h2>
              <p className="text-lg text-gray-600">
                Google reviews for dental practices — how SmileConsent helps you ask, track, and
                protect your reputation.
              </p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <details
                  key={faq.question}
                  className="group bg-white rounded-xl border border-gray-200 p-5 open:shadow-md animate-on-scroll"
                >
                  <summary className="cursor-pointer list-none font-semibold text-gray-900 flex items-start justify-between gap-4">
                    <span>{faq.question}</span>
                    <span className="text-blue-600 group-open:rotate-90 transition-transform shrink-0 mt-0.5">
                      <ArrowRightIcon className="h-5 w-5" aria-hidden />
                    </span>
                  </summary>
                  <p className="mt-3 text-gray-600 leading-relaxed">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-r from-blue-500 to-indigo-500">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to grow your Google reviews?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Join UK dental practices using SmileConsent for consent and reputation. Start your
              free trial, connect your Google review link, and send your first request after the
              next signed consent.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-white text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <SparklesIcon className="h-6 w-6" aria-hidden />
                Start Free Trial
              </Link>
              <button
                type="button"
                onClick={() => navigate('/book-demo')}
                className="border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
              >
                <CalendarIcon className="h-6 w-6" aria-hidden />
                Book Demo
              </button>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <InternalLinks
              title="Related SmileConsent solutions"
              links={[
                {
                  title: 'Dental consent software',
                  description: 'AI-assisted consent, digital signing, and audit trails for UK practices',
                  path: '/dental-consent-software',
                },
                {
                  title: 'Digital consent for dentists',
                  description: 'Paperless consent workflows patients complete on any device',
                  path: '/digital-consent-dentists',
                },
                {
                  title: 'Dental consent forms',
                  description: 'Treatment-ready digital consent forms for modern practices',
                  path: '/dental-consent-forms',
                },
                {
                  title: 'Dental compliance software',
                  description: 'Documentation support aligned with UK practice expectations',
                  path: '/dental-compliance-software',
                },
              ]}
            />
          </div>
        </section>
      </div>
    </>
  );
}
